import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, DialogTrigger, Modal, Dialog } from 'react-aria-components';
import { SearchFacet, ApplyPendingFiltersCallback } from '../../../types/search';
import PortalSearch from './PortalSearch';
import PortalFilterWrapper from './PortalFilterWrapper';
import PortalDateTypeSelector from './PortalDateTypeSelector';
import PortalSelectDateButton from './PortalSelectDateButton';
import { useSearchConfig } from '../../Search/hooks/useSearchConfig';
import eventBus, { CUSTOM_EVENTS } from '../../utils/EventBus';
import SvgIcon from '../../utils/SvgIcon';
import DownloadDocsBulk from './DownloadDocsBulk';
import { useDateFilter } from '../contexts/PortalDateFilterProvider';
import { formatDateRangeLabel } from '../utils/calendarDateUtils';
import { CALENDAR_DATE_FACET } from '..';

type PortalFiltersProps = Readonly<{
  facetGroups: SearchFacet[];
  appliedFilters: SearchFacet[];
  applyPendingFilters: ApplyPendingFiltersCallback;
  hasFiltersApplied: boolean;
  clearAllFilters: () => void;
  searchTerm: string | null;
  updateSearchTerm: (searchTerm: string) => void;
  listenToDatepickerEvent?: boolean;
  sortByOptions: { key: string; label: string; options?: Record<string, string> }[];
  onSortChange: (newSortOrder: string, newSortCol: string) => void;
  sortOrder: string;
  sortCol: string;
  showBulkDownload: boolean;
  selectedRows: string[];
  isError: boolean;
  isSuccess: boolean;
  resultsCount?: number;
}>;

export default function PortalFilters({
  facetGroups,
  appliedFilters,
  applyPendingFilters,
  hasFiltersApplied,
  clearAllFilters,
  searchTerm,
  updateSearchTerm,
  listenToDatepickerEvent = false,
  sortByOptions,
  onSortChange,
  sortOrder,
  sortCol,
  showBulkDownload,
  selectedRows,
  isError,
  isSuccess,
  resultsCount,
}: PortalFiltersProps) {
  const { labels } = useSearchConfig();
  const [pendingFilterState, setPendingFilterState] = useState<Record<string, string | string[]> | null>(null);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [filtersHeading, setFiltersHeading] = useState<string>('Filters');
  const [currentDateFacet, setCurrentDateFacet] = useState<SearchFacet | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const openedViaEventRef = useRef<boolean>(false);
  const facetGroupsRef = useRef<SearchFacet[]>(facetGroups);
  const filterToggleRef = useRef<HTMLButtonElement>(null);
  // Access date filter for calendar date facet to sync when opening via event
  const dateFromFilter = useDateFilter(CALENDAR_DATE_FACET);

  // Keep the ref up to date
  useEffect(() => {
    facetGroupsRef.current = facetGroups;
  }, [facetGroups]);
  const [currentSortOrder, setCurrentSortOrder] = useState<string>(sortOrder);
  const [currentSortCol, setCurrentSortCol] = useState<string>(sortCol);
  const disableFilterBtn = isError || isSuccess === false || (resultsCount !== undefined && resultsCount === 0);

  // Sync state with props when they change
  React.useEffect(() => {
    setCurrentSortOrder(sortOrder);
    setCurrentSortCol(sortCol);
  }, [sortOrder, sortCol]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      setDatePickerOpen(false);
      openedViaEventRef.current = false;
    }
  }, [isDialogOpen]);

  const handleFilterChange = useCallback((filter: string, values: string | string[]) => {
    setPendingFilterState(prev => {
      const updated = prev === null ? {} : { ...prev };
      updated[filter] = values;
      return updated;
    });
  }, []);

  const handleSearch = useCallback(() => {
    if (pendingFilterState !== null) {
      applyPendingFilters(pendingFilterState);
      setPendingFilterState(null);
    }
    if (onSortChange && (currentSortCol !== sortCol || currentSortOrder !== sortOrder)) {
      onSortChange(currentSortOrder || '', currentSortCol || '');
    }
    closeDialogWithAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFilterState, onSortChange, currentSortCol, sortCol, currentSortOrder, sortOrder, applyPendingFilters]);

  const handleClear = useCallback(() => {
    clearAllFilters();
    setPendingFilterState(null);
  }, [clearAllFilters]);

  const closeDialogWithAnimation = useCallback(() => {
    // React Aria's Modal will handle the exit animation via data-exiting
    setIsDialogOpen(false);
    document.body.classList.remove('dynamic-filters__scroll-lock');
    filterToggleRef.current?.focus();
  }, []);

  const handleCloseClick = useCallback(() => {
    if (openedViaEventRef.current) {
      // If datepicker was opened via event, close everything directly
      setDatePickerOpen(false);
      openedViaEventRef.current = false;
    }
    closeDialogWithAnimation();
  }, [closeDialogWithAnimation]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Close if clicking directly on the menu overlay (fullscreen) but not on any child elements
      // This allows clicking outside the container to dismiss the dialog
      if (e.target === e.currentTarget) {
        closeDialogWithAnimation();
      }
    },
    [closeDialogWithAnimation]
  );

  const backButtonHandler = useCallback(() => {
    setDatePickerOpen(false);
    setFiltersHeading('Filters');
  }, []);

  /**
   * Helper function to get date value from pending filters or URL
   */
  const getDateValueFromState = useCallback(
    (facetKey: string): string | string[] | null => {
      // Check pending filters first
      if (pendingFilterState?.[facetKey]) {
        const pendingValue = pendingFilterState[facetKey];
        return pendingValue;
      }

      return null;
    },
    [pendingFilterState]
  );

  /**
   * Helper function to update date filter with value
   */
  const updateDateFilter = useCallback(
    (dateValue: string) => {
      dateFromFilter.setSelectedValue(dateValue);
      const dates = dateValue.split(',');
      if (dates.length >= 2) {
        try {
          const label = formatDateRangeLabel(dates[0], dates[1]);
          dateFromFilter.setSelectedValueLabel(label);
        } catch {
          // Silently handle formatting errors
        }
      }
    },
    [dateFromFilter]
  );

  /**
   * Custom events logic for interacting with CalendarView
   * components that sit outside the filter components tree.
   */
  useEffect(() => {
    if (!listenToDatepickerEvent) {
      return;
    }

    const openDatepicker = () => {
      const dateFromFacet = facetGroupsRef.current.find(facet => facet.key === CALENDAR_DATE_FACET);

      // If facet data is missing (can happen when no results), reset filters to restore state
      if (!dateFromFacet) {
        clearAllFilters();
        return;
      }

      // Open the filter menu and datepicker directly (bypassing the filter list)
      setIsDialogOpen(true);
      document.body.classList.add('dynamic-filters__scroll-lock');
      setCurrentDateFacet(dateFromFacet);
      setDatePickerOpen(true);
      setFiltersHeading(dateFromFacet.heading);
      openedViaEventRef.current = true;
    };
    eventBus.on(CUSTOM_EVENTS.PORTAL_OPEN_DATEPICKER_DRAWER, openDatepicker);

    return () => {
      eventBus.off(CUSTOM_EVENTS.PORTAL_OPEN_DATEPICKER_DRAWER, openDatepicker);
    };
  }, [listenToDatepickerEvent, getDateValueFromState, updateDateFilter, clearAllFilters]);

  /**
   * Event listener to clear filters, search is cleared in PortalSearch component
   */
  useEffect(() => {
    eventBus.on(CUSTOM_EVENTS.PORTAL_CLEAR_FILTERS_AND_SEARCH, clearAllFilters);

    return () => {
      eventBus.off(CUSTOM_EVENTS.PORTAL_CLEAR_FILTERS_AND_SEARCH, clearAllFilters);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isApplyBtnDisabled = React.useMemo(() => {
    if (pendingFilterState !== null) return false;
    if (currentSortCol !== sortCol || currentSortOrder !== sortOrder) return false;
    return true;
  }, [pendingFilterState, currentSortCol, currentSortOrder, sortCol, sortOrder]);

  return (
    <>
      <div className="dynamic-filters">
        {showBulkDownload && <DownloadDocsBulk placement="bottom start" selectedRows={selectedRows} />}
        <div className="dynamic-filters__btns-wrap">
          <Button
            className="dynamic-filters__open-button cta cta--secondary cta--small"
            onClick={() => setSearchOpen(!searchOpen)}
            aria-label="Toggle search bar"
            aria-expanded={searchOpen}
          >
            <SvgIcon
              iconName={searchOpen ? 'x-mark-medium' : 'search-medium'}
              additionalClasses="dynamic-filters__open-button-icon"
              aria-hidden="true"
            />
          </Button>
          <DialogTrigger isOpen={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <Button
              ref={filterToggleRef}
              className="dynamic-filters__open-button cta cta--secondary cta--small"
              aria-label={labels.filterBtnLabel}
              isDisabled={disableFilterBtn}
            >
              <svg className="dynamic-filters__open-button-icon icon" aria-hidden="true">
                <use href="/dist/assets/icons/sprite.svg#filter"></use>
              </svg>
              <span className="dynamic-filters__open-button-text">{labels.filterBtnLabel}</span>
            </Button>
            <Modal className="dynamic-filters__modal">
              <Dialog
                className="dynamic-filters__menu"
                id="dynamic-filters-menu"
                aria-label="Filter menu"
                onClick={handleBackdropClick}
              >
                <div className="dynamic-filters__container">
                  <div
                    className={`dynamic-filters__header${datePickerOpen ? ' dynamic-filters__header--date-picker-open' : ''}`}
                  >
                    <div className="dynamic-filters__header-left-col">
                      {datePickerOpen && (
                        <Button
                          className="dynamic-filters__back-btn cta cta--tertiary"
                          aria-label="Back to filters"
                          onClick={backButtonHandler}
                        >
                          <svg className="dynamic-filters__back-btn-icon icon" aria-hidden="true">
                            <use href="/dist/assets/icons/sprite.svg#arrow-left"></use>
                          </svg>
                          Back
                        </Button>
                      )}
                      <h2 className="dynamic-filters__heading">{filtersHeading}</h2>
                    </div>
                    <div className="dynamic-filters__buttons">
                      {!datePickerOpen && (
                        <Button
                          className="dynamic-filters__clear"
                          isDisabled={!hasFiltersApplied}
                          onClick={handleClear}
                        >
                          Clear all
                        </Button>
                      )}
                      <Button className="dynamic-filters__close" onClick={handleCloseClick} aria-label="Close filters">
                        <svg className="dynamic-filters__close-icon icon" aria-hidden="true">
                          <use href="/dist/assets/icons/sprite.svg#x-mark"></use>
                        </svg>
                      </Button>
                    </div>
                  </div>
                  <div className="dynamic-filters__body">
                    {!datePickerOpen && (
                      <PortalFilterWrapper
                        facetGroups={facetGroups}
                        appliedFilters={appliedFilters}
                        pendingFilterState={pendingFilterState}
                        handleFilterChange={handleFilterChange}
                        setFilterDatePickerOpen={setDatePickerOpen}
                        setFiltersHeading={setFiltersHeading}
                        setCurrentDateFacet={setCurrentDateFacet}
                        sortByOptions={sortByOptions}
                        sortOrder={sortOrder}
                        sortCol={sortCol}
                        setCurrentSortCol={setCurrentSortCol}
                        setCurrentSortOrder={setCurrentSortOrder}
                        labels={labels}
                      />
                    )}
                    {datePickerOpen && !!currentDateFacet && (
                      <PortalDateTypeSelector currentDateFacet={currentDateFacet} />
                    )}
                    <div className="dynamic-filters__footer">
                      {!datePickerOpen && (
                        <Button
                          className="cta cta--primary cta--small"
                          isDisabled={isApplyBtnDisabled}
                          onClick={handleSearch}
                        >
                          {labels.applyFilterBtnLabel}
                        </Button>
                      )}
                      {datePickerOpen && (
                        <PortalSelectDateButton
                          setDatePickerOpen={setDatePickerOpen}
                          setFiltersHeading={setFiltersHeading}
                          currentDateFacet={currentDateFacet}
                          pendingFilterState={pendingFilterState}
                          appliedFilters={appliedFilters}
                          handleFilterChange={handleFilterChange}
                        >
                          Select date
                        </PortalSelectDateButton>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog>
            </Modal>
          </DialogTrigger>
        </div>
      </div>
      <PortalSearch
        open={searchOpen}
        setOpen={setSearchOpen}
        searchTerm={searchTerm}
        updateSearchTerm={updateSearchTerm}
      />
    </>
  );
}
